import { useEffect, useState } from "react";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Key, Trash, Plus, X, CheckCircle, Warning, Clock, Buildings, ClipboardText } from "@phosphor-icons/react";

export default function SyncSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState("");
  const [showCCModal, setShowCCModal] = useState(null);  // { company_code }

  const load = async () => {
    try {
      const { data } = await api.get("/finance/sync-settings");
      setData(data);
    } catch (err) { toast.error("Failed to load sync settings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const genKey = async () => {
    if (data?.has_key && !window.confirm("A key is already active. Generating a new one will revoke the old key immediately. Continue?")) return;
    try {
      const { data: res } = await api.post("/finance/sync-settings/generate-key");
      setNewKey(res.api_key);
      toast.success("New sync key generated — copy it NOW");
      load();
    } catch (err) { toast.error("Failed to generate key"); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke the current sync key? The desktop agent will stop working until you generate and paste a new key.")) return;
    await api.delete("/finance/sync-settings/key");
    toast.success("Key revoked");
    load();
  };

  if (loading) return <Layout><div className="p-6 text-sm text-[#717680]">Loading sync settings...</div></Layout>;
  if (!data) return <Layout><div className="p-6 text-sm text-[#D90429]">Access denied</div></Layout>;

  const agent = data.agent || {};
  const agentIsStale = agent.last_seen && (new Date() - new Date(agent.last_seen)) > 3600_000;  // 1h
  const agentStatusColor = !agent.last_seen ? "text-[#717680]" :
    (agentIsStale || agent.status === "error") ? "text-[#D90429]" :
    agent.status === "check" ? "text-[#CA8A04]" : "text-[#0D9488]";

  return (
    <Layout>
      <div className="px-6 py-6 min-h-screen bg-[#F5F3EF]">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A0A0A]">Finance Sync Settings</h1>
          <div className="mt-1 text-[12px] text-[#717680]">BUSY → Reyansh accounting sync · desktop agent credentials, status and company data counts.</div>
        </div>

        {/* API Key panel */}
        <section className="bg-white border border-black/10 p-5 mb-5" data-testid="syncsettings-key-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold flex items-center gap-2"><Key size={20} weight="duotone" className="text-[#D90429]" /> API Key</h2>
            {data.has_key && <span className="text-[11px] font-mono-shelv text-[#0D9488]">ACTIVE · {data.key_fingerprint}</span>}
          </div>
          {newKey && (
            <div className="mb-3 border border-[#D90429]/40 bg-[#FFE5E9]/40 p-3" data-testid="sync-new-key-banner">
              <div className="text-[11px] uppercase font-mono-shelv tracking-widest text-[#D90429] font-bold">⚠ SHOWN ONCE — COPY NOW</div>
              <div className="mt-1 font-mono text-sm break-all select-all" data-testid="sync-new-key-value">{newKey}</div>
              <div className="mt-2 text-[11px] text-[#717680]">The server stores only a SHA-256 hash. Paste this into the sync agent's config file, then dismiss this banner.</div>
              <button onClick={() => setNewKey("")} className="mt-2 text-[11px] uppercase font-mono-shelv tracking-widest text-[#D90429] hover:underline">I've copied it, dismiss</button>
            </div>
          )}
          {!data.has_key && !newKey && <div className="text-sm text-[#717680] mb-3 italic">No active key. Generate one and paste it into the sync agent's config.</div>}
          <div className="flex gap-2">
            <button onClick={genKey} data-testid="sync-gen-key-btn" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] flex items-center gap-2">
              <Key size={14} weight="bold" /> {data.has_key ? "Rotate key" : "Generate sync key"}
            </button>
            {data.has_key && (
              <button onClick={revoke} data-testid="sync-revoke-btn" className="px-4 py-2 border border-black/20 text-sm hover:bg-black hover:text-white flex items-center gap-2">
                <Trash size={14} /> Revoke
              </button>
            )}
          </div>
        </section>

        {/* Agent status */}
        <section className="bg-white border border-black/10 p-5 mb-5" data-testid="syncsettings-agent-panel">
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Clock size={20} weight="duotone" className="text-[#D90429]" /> Agent Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <KV label="Last seen" value={agent.last_seen ? new Date(agent.last_seen).toLocaleString("en-IN") : "Never"} valueClass={agentStatusColor} />
            <KV label="Status" value={(agent.status || "-").toUpperCase()} valueClass={agentStatusColor} />
            <KV label="Host" value={agent.host || "-"} />
            <KV label="Agent version" value={agent.agent_version || "-"} />
          </div>
          {agent.detail && <div className="mt-3 text-xs text-[#717680]"><b>Last detail:</b> {agent.detail}</div>}
          {agentIsStale && <div className="mt-3 text-xs text-[#D90429] flex items-center gap-1"><Warning size={14} weight="fill" /> Agent hasn't checked in for over an hour (expected every 15 min on a working day).</div>}
        </section>

        {/* Companies & FY counts */}
        <section className="bg-white border border-black/10 p-5 mb-5" data-testid="syncsettings-companies-panel">
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Buildings size={20} weight="duotone" className="text-[#D90429]" /> Companies & Financial Years</h2>
          {(data.companies || []).length === 0 ? (
            <div className="text-sm text-[#717680] italic">No company data has arrived yet. Once the sync agent sends its first batch, companies and voucher counts show here.</div>
          ) : (
            <div className="space-y-4">
              {data.companies.map((c) => (
                <div key={c.company_code} className="border border-black/10 bg-[#FAFAF7] p-3" data-testid={`sync-company-${c.company_code}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{c.Name || c.company_code} <span className="font-mono-shelv text-[10px] text-[#717680] ml-1">· {c.company_code}</span></div>
                      <div className="text-[11px] text-[#717680]">GSTIN: {c.GSTNo || "-"} · Books begin: {c.BegFY || "-"}</div>
                    </div>
                    <button onClick={() => setShowCCModal({ company_code: c.company_code })} className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#D90429] hover:underline">Set bank CC limits</button>
                  </div>
                  <table className="w-full mt-3 text-xs">
                    <thead className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]">
                      <tr><th className="text-left py-1">FY</th><th className="text-right">Vouchers</th><th className="text-right">Masters</th><th className="text-right">Last received</th></tr>
                    </thead>
                    <tbody>
                      {(c.fys || []).length === 0 ? (
                        <tr><td colSpan={4} className="py-2 text-[#717680] italic">No vouchers yet.</td></tr>
                      ) : c.fys.map((f) => (
                        <tr key={f.fy} className="border-t border-black/5">
                          <td className="py-1 font-mono-shelv">{f.fy}</td>
                          <td className="text-right">{f.vouchers.toLocaleString("en-IN")}</td>
                          <td className="text-right">{f.masters.toLocaleString("en-IN")}</td>
                          <td className="text-right text-[11px] text-[#717680]">{f.last_received ? new Date(f.last_received).toLocaleString("en-IN") : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* CC limits summary */}
        <section className="bg-white border border-black/10 p-5" data-testid="syncsettings-cc-panel">
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><ClipboardText size={20} weight="duotone" className="text-[#D90429]" /> Bank CC Limits</h2>
          <div className="text-[11px] text-[#717680] mb-3">Credit limits per bank-O/D ledger. Snapshot shows CC used vs limit.</div>
          {(data.cc_limits || []).length === 0 ? (
            <div className="text-sm text-[#717680] italic">No limits set yet. Click "Set bank CC limits" on any company above.</div>
          ) : (
            <div className="space-y-1">
              {data.cc_limits.map((l) => (
                <div key={`${l.company_code}-${l.bank_master_code}`} className="flex items-center justify-between text-xs border border-black/10 bg-[#FAFAF7] p-2">
                  <div><span className="font-mono-shelv text-[10px] text-[#717680]">{l.company_code}</span> · bank code <span className="font-mono">{l.bank_master_code}</span></div>
                  <div className="font-semibold">₹{Number(l.limit || 0).toLocaleString("en-IN")}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {showCCModal && (
          <CCLimitModal onClose={() => setShowCCModal(null)} companyCode={showCCModal.company_code} onSaved={() => { setShowCCModal(null); load(); }} />
        )}
      </div>
    </Layout>
  );
}

function CCLimitModal({ onClose, companyCode, onSaved }) {
  const [bankCode, setBankCode] = useState("");
  const [limit, setLimit] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!bankCode || !limit) { toast.error("Bank master code + limit required"); return; }
    setSaving(true);
    try {
      await api.put("/finance/sync-settings/cc-limits", {
        company_code: companyCode,
        bank_master_code: Number(bankCode),
        limit: Number(limit),
      });
      toast.success("Limit saved");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="cc-limit-modal">
      <div className="bg-white border border-black/15 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold">Set CC Limit · {companyCode}</h3>
          <button onClick={onClose} className="text-[#717680]"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]">Bank/O-D master code (number)</label>
            <input data-testid="cc-bank-code" value={bankCode} onChange={(e) => setBankCode(e.target.value)} placeholder="e.g. 128" className="w-full border border-black/15 px-3 py-2 text-sm font-mono" />
            <div className="text-[10px] text-[#717680] mt-1">The ParentGrp=128 ledger code shown in BUSY (Bank O/D Account).</div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]">Sanctioned limit (₹)</label>
            <input data-testid="cc-limit" type="number" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="e.g. 25000000" className="w-full border border-black/15 px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="cc-save-btn" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold disabled:opacity-40">{saving ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, valueClass = "" }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]">{label}</div>
      <div className={`text-sm text-[#0A0A0A] ${valueClass}`}>{value}</div>
    </div>
  );
}
